import { Button } from "@/components/ui/button";
import { Plus, Database } from "lucide-react";
import { useSettings } from "@/providers/SettingsProvider";
import { generateBelgianCandidateData } from "@/utils/candidate/operations";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface CandidatesActionsProps {
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
  fetchCandidates: () => Promise<void>;
  onAddCandidate: () => void;
}

export const CandidatesActions = ({
  isGenerating,
  setIsGenerating,
  fetchCandidates,
  onAddCandidate
}: CandidatesActionsProps) => {
  const { t } = useTranslation();
  // Use try-catch to handle the case where SettingsProvider is not available
  let devMode = false;
  try {
    const { devMode: settingsDevMode } = useSettings();
    devMode = settingsDevMode;
  } catch (error) {
    console.error("SettingsProvider not available:", error);
    // Continue with default value for devMode
  }

  const handleGenerateDummyData = async () => {
    try {
      setIsGenerating(true);
      await generateBelgianCandidateData();
      // Refresh candidates list after generating data
      await fetchCandidates();
    } catch (error) {
      console.error("Failed to generate candidate data:", error);
      toast({
        title: t("candidates.actions.errorTitle"),
        description: t("candidates.actions.errorGenerate"),
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex gap-2">
      {devMode && (
        <Button 
          variant="outline" 
          onClick={handleGenerateDummyData}
          disabled={isGenerating}
          className="flex items-center gap-2"
        >
          <Database className="h-4 w-4" />
          {isGenerating ? t("candidates.actions.generating") : t("candidates.actions.generateDummy")}
        </Button>
      )}
      <Button 
        onClick={onAddCandidate} 
        className="bg-digger-blue hover:bg-digger-blue-light"
      >
        <Plus className="h-4 w-4 mr-2" />
        {t("candidates.actions.newCandidate")}
      </Button>
    </div>
  );
};
